import * as React from 'react';
import { useLazyQuery } from "@apollo/react-hooks";
import gql from "graphql-tag";
import { AsyncLoadPagedResult, AsyncLoadPagedOptions } from '../../../../shared/abstractStore';
import { useFetchMoreCallback } from '../../../../shared/useFetchMoreCallback';
import { userAssessmentAssignmentsListViewBaseViewModelQuery, userAssessmentAssignmentsListViewBaseViewModelQueryVariables } from '../../generated/userAssessmentAssignmentsListViewBaseViewModelQuery';
import { mainApiConfig } from '../../../../configure/mainApiConfig';
import { subscriptionAssessmentFieldsFragment } from '../../models/SubscriptionAssessment';
import { AssessmentType } from '../../models/codeOnly/AssessmentType';
import { assessmentFieldsFragment } from '../../models/Assessment';
import { Guid } from 'guid-string';
import { userAssessmentAssignmentFieldsFragment } from '../../models/UserAssessmentAssignment';
import { subscriptionTeamFieldsFragment } from '../../models/SubscriptionTeam';
import { profileFieldsFragment } from '../../models/Profile';
import { driverMetricRecommendationFieldsFragment } from '../../models/DriverMetricRecommendation';
import { userDriverMetricFieldsFragment } from '../../models/UserDriverMetric';
import { driverMetricFieldsFragment } from '../../models/DriverMetric';


export interface UserAssessmentAssignmentsListViewBaseViewModelAsyncLoadPageOptions extends AsyncLoadPagedOptions {
    assessmentTypes?: Array<AssessmentType>,
    userId?: string | undefined | null,
    subscriptionTeamId?: string | undefined | null,
}

/**
 * Get a list of subscriptionAssessments from the store.
 * @param options
 */
export function useUserAssessmentAssignmentsListViewBaseViewModel(subscriptionId: string | null | undefined, options: UserAssessmentAssignmentsListViewBaseViewModelAsyncLoadPageOptions = {}): AsyncLoadPagedResult<userAssessmentAssignmentsListViewBaseViewModelQuery> {
    // Query data we need from the api.
    const [load, { data, loading, error, refetch, fetchMore: fetchMoreFromStore }] = useLazyQuery<userAssessmentAssignmentsListViewBaseViewModelQuery, userAssessmentAssignmentsListViewBaseViewModelQueryVariables>(
        gql`
        query userAssessmentAssignmentsListViewBaseViewModelQuery ($subscriptionId: ID!, $offset: Int, $first: Int, $assessmentTypes: [String!], $userId: ID, $subscriptionTeamId: ID, $userIdOrEmptyGuid: ID!, $subscriptionTeamIdOrEmptyGuid: ID!) {
            items: userAssessmentAssignments (userId: $userId, subscriptionTeamId: $subscriptionTeamId) {
                ...userAssessmentAssignmentFields
            }

            subscriptionAssessments (subscriptionId: $subscriptionId) {
                ...subscriptionAssessmentFields
            }

            assessments (offset: $offset, first: $first, assessmentTypes: $assessmentTypes) {
                ...assessmentFields
            }

            subscriptionTeam (id: $subscriptionTeamIdOrEmptyGuid) {
                ...subscriptionTeamFields
            }

            profile (userId: $userIdOrEmptyGuid) {
                ...profileFields
            }

            driverMetricRecommendations (targetType: "Assessment") {
                ...driverMetricRecommendationFields
            }

            driverMetrics {
                ...driverMetricFields
            }

            userDriverMetrics (userId: $userIdOrEmptyGuid) {
                ...userDriverMetricFields
            }
        }

        ${userAssessmentAssignmentFieldsFragment}
        ${subscriptionAssessmentFieldsFragment}
        ${assessmentFieldsFragment}
        ${subscriptionTeamFieldsFragment}
        ${profileFieldsFragment}
        ${driverMetricRecommendationFieldsFragment}
        ${driverMetricFieldsFragment}
        ${userDriverMetricFieldsFragment}
        `,
        {

            variables: {
                offset: options.startOffset,
                first: options.pageSize,
                assessmentTypes: options.assessmentTypes,
                subscriptionId: subscriptionId ?? Guid.empty,
                userId: options.userId,
                userIdOrEmptyGuid: options.userId ?? Guid.empty,
                subscriptionTeamId: options.subscriptionTeamId,
                subscriptionTeamIdOrEmptyGuid: options.subscriptionTeamId ?? Guid.empty,
            },
            fetchPolicy: options.fetchPolicy ?? mainApiConfig.defaultFetchPolicy,
            pollInterval: options.pollInterval,
            onCompleted: (data) => {
                if (options.onCompleted) {
                    options.onCompleted(data);
                }
            }
        }
    );

    // Run the query (unless we are being lazy).
    React.useEffect(() => {
        if (!options.lazy) {
            load();
        }
    }, [options.lazy, load])

    // Get the functions used to manage paging.
    const { fetchMore, hasMore: _hasMore } = useFetchMoreCallback<userAssessmentAssignmentsListViewBaseViewModelQuery>(
        fetchMoreFromStore,
        (options?.startOffset ?? 0) + (data?.items?.length ?? 0),
        (prevResults, newResults) => ({
            ...prevResults,
            ...newResults,

            items: newResults?.items ?? [],
            subscriptionAssessments: newResults?.subscriptionAssessments ?? [],
            assessments: [...(prevResults?.assessments ?? []), ...(newResults?.assessments ?? [])],
            profile: newResults?.profile ?? null,
            subscriptionTeam: newResults?.subscriptionTeam ?? null,
            driverMetricRecommendations: newResults?.driverMetricRecommendations ?? [],
            driverMetrics: newResults?.driverMetrics ?? [],
            userDriverMetrics: newResults?.userDriverMetrics ?? [],
        }),
        (newResults) => newResults?.items?.length
    );
    const hasMore = React.useCallback(() => !options.pageSize ? false : _hasMore(), [options.pageSize, _hasMore]);
    
    // Return the results.
    const refresh = React.useCallback(async () => { !refetch ? await load() : await refetch(); }, [refetch, load]);
    return React.useMemo(() => ({
        data: data ?? ({} as any),
        refresh: refresh,
        isLoading: loading,
        errors: error,
        fetchMore: fetchMore,
        hasMore: hasMore,
    }), [data, refresh, loading, error, fetchMore, hasMore]);
}
